import React from "react";
import { graphql } from "react-relay";
import type { OnChangeValue } from "react-select";

import cx from "classnames";
import { fetchQuery } from "relay-runtime";

import { type Channel, type ChannelNode, SlackChannelSelect } from "~/common/SlackChannelSelect";
import { environment } from "~/models";
import toast from "~/ui/toast";

import type { SlackQuery as QueryType } from "~/__generated__/SlackQuery.graphql";

import stylesMeetingReminders from "~/stream/StreamSettings/Automations/MeetingReminders.module.css";
import stylesTimeSaver from "~/stream/StreamSettings/Automations/TimeSaver.module.css";

import styles from "./Slack.module.css";

const SlackQuery = graphql`
	query SlackQuery {
		viewer {
			integrations {
				slack {
					scopes
				}
			}
		}
	}
`;

export function slackScopesAreValid(scopes: string) {
	return scopes.includes("chat:write") && scopes.includes("channels:read");
}

export function onSlackAuthFailed(data: { message: string }) {
	if (data.message) {
		toast.error(data.message);
	} else {
		toast.error("Slack authentication is required");
	}
}

export function refetchUserSlackIntegration(callback: () => void) {
	fetchQuery<QueryType>(environment, SlackQuery, {}, { fetchPolicy: "network-only" })
		.toPromise()
		.then(data => {
			const slacks = data?.viewer.integrations.slack;
			const scopes = (slacks && slacks[0]?.scopes) ?? "";
			const viewerHasSlack = slackScopesAreValid(scopes);

			if (viewerHasSlack) {
				callback();
			} else {
				toast.error("Something went wrong");
			}
		});
}

interface Props {
	channel: ChannelNode;
	onChange: (v: ChannelNode) => void;
}

export default function SlackInputs({ channel, onChange: _onChange }: Props) {
	const value = React.useMemo(() => {
		if (channel.id) {
			return { ...channel, label: channel.name, value: channel.name };
		}

		return undefined;
	}, [channel]);

	const onChange = React.useCallback(
		(v: OnChangeValue<Channel, false>) => {
			if (v) {
				_onChange({ id: v.id, teamId: v.teamId, name: v.name, isPrivate: v.isPrivate, isUser: v.isUser });
			}
		},
		[_onChange],
	);

	return (
		<>
			<label
				className={cx(
					stylesMeetingReminders.row,
					stylesTimeSaver.row,
					stylesMeetingReminders.tight,
					stylesTimeSaver.label,
				)}
			>
				Select a user or channel:
			</label>
			<div className={cx(stylesMeetingReminders.row, stylesTimeSaver.row, stylesMeetingReminders.tight)}>
				<SlackChannelSelect onChange={onChange} placeholder="Slack" className={styles.slackSelect} value={value} />
			</div>
			<p className={cx(stylesMeetingReminders.row, stylesTimeSaver.row, styles.helpText)}>
				To send to a private channel ‘/invite @Fellow’ to channel first
			</p>
		</>
	);
}
